import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { WorkspaceBadge } from "../components/WorkspaceBadge";
import { WorkspaceSwitcher } from "../components/WorkspaceSwitcher";
import {
  createScene,
  deleteScene,
  duplicateScene,
  listScenes,
  listWorkspaces,
  patchScene,
  type Scene,
  type Workspace,
} from "../lib/plbxApi";

const PAGE_LIMIT = 50;

const fmtDate = (iso: string) => {
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
};

export const BoardsListPage: React.FC = () => {
  const navigate = useNavigate();
  const [scenes, setScenes] = useState<Scene[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [pinnedOnly, setPinnedOnly] = useState(false);
  const [creating, setCreating] = useState(false);
  // Inline rename state — null = not editing; otherwise scene id.
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState("");
  const [busyId, setBusyId] = useState<string | null>(null);

  // Workspace state
  const [workspaces, setWorkspaces] = useState<Workspace[]>([]);
  const [activeWs, setActiveWs] = useState<string | null>(null);
  const [moveOpen, setMoveOpen] = useState<Scene | null>(null);
  const [currentUserEmail, setCurrentUserEmail] = useState<string | null>(null);

  // Load workspaces and decide active on mount
  useEffect(() => {
    (async () => {
      try {
        const res = await listWorkspaces();
        setWorkspaces(res.data);
        const queryWs = new URLSearchParams(window.location.search).get("workspace");
        let chosen: Workspace | undefined;
        if (queryWs) {
          chosen = res.data.find((w) => w.slug === queryWs);
        }
        if (!chosen) {
          const stored = localStorage.getItem("plbx_active_workspace");
          if (stored) chosen = res.data.find((w) => w.id === stored);
        }
        if (!chosen) {
          chosen = res.data.find((w) => w.kind === "shared") ?? res.data[0];
        }
        if (chosen) {
          setActiveWs(chosen.id);
          localStorage.setItem("plbx_active_workspace", chosen.id);
        }
      } catch (e: any) {
        setError(e?.message || "failed to load workspaces");
      }
    })();
  }, []);

  const reload = useCallback(async () => {
    if (!activeWs) return;
    setLoading(true);
    setError(null);
    try {
      const res = await listScenes({ limit: PAGE_LIMIT, workspaceId: activeWs });
      setScenes(res.data);
      // Capture caller email from a personal-workspace listing: every row
      // there is creator==caller by construction. Stopgap until a /me
      // endpoint exists. Without this the Move button never appears for
      // users who haven't created a board this session.
      const activeWsObj = workspaces.find((w) => w.id === activeWs);
      if (activeWsObj?.kind === "personal" && res.data.length > 0) {
        setCurrentUserEmail((prev) => prev ?? res.data[0].ownerEmail);
      }
    } catch (e: any) {
      setError(e?.message || "failed to load boards");
    } finally {
      setLoading(false);
    }
  }, [activeWs, workspaces]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return scenes.filter((s) => {
      if (pinnedOnly && !s.pinned) return false;
      if (q && !s.name.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [scenes, search, pinnedOnly]);

  const onSelectWs = (id: string) => {
    setActiveWs(id);
    localStorage.setItem("plbx_active_workspace", id);
    const ws = workspaces.find((w) => w.id === id);
    if (ws) {
      const url = new URL(window.location.href);
      url.searchParams.set("workspace", ws.slug);
      window.history.replaceState({}, "", url.toString());
    }
  };

  const isOwner = (s: Scene) =>
    currentUserEmail != null && s.ownerEmail === currentUserEmail;

  const activeWsName = workspaces.find((w) => w.id === activeWs)?.name ?? "Boards";

  const onCreate = async () => {
    if (!activeWs) return;
    setCreating(true);
    try {
      const scene = await createScene({ name: "Untitled board", workspaceId: activeWs });
      setCurrentUserEmail((prev) => prev ?? scene.ownerEmail);
      localStorage.setItem("plbx_pending_board", scene.id);
      window.location.assign("/");
    } catch (e: any) {
      setError(e?.message || "failed to create board");
    } finally {
      setCreating(false);
    }
  };

  const onTogglePin = async (s: Scene) => {
    try {
      const updated = await patchScene(s.id, { pinned: !s.pinned });
      setScenes((prev) => prev.map((x) => (x.id === s.id ? updated : x)));
    } catch (e: any) {
      setError(e?.message || "failed to update");
    }
  };

  const startRename = (s: Scene) => {
    setEditingId(s.id);
    setEditDraft(s.name);
  };

  const commitRename = async (s: Scene) => {
    const next = editDraft.trim();
    setEditingId(null);
    if (!next || next === s.name) return;
    try {
      const updated = await patchScene(s.id, { name: next });
      setScenes((prev) => prev.map((x) => (x.id === s.id ? updated : x)));
    } catch (e: any) {
      setError(e?.message || "failed to rename");
    }
  };

  const cancelRename = () => {
    setEditingId(null);
    setEditDraft("");
  };

  const onDuplicate = async (s: Scene) => {
    if (!activeWs) return;
    setBusyId(s.id);
    try {
      const copy = await duplicateScene(s, activeWs);
      setScenes((prev) => [copy, ...prev]);
    } catch (e: any) {
      setError(e?.message || "failed to duplicate");
    } finally {
      setBusyId(null);
    }
  };

  const onDelete = async (s: Scene) => {
    if (!confirm(`Delete board "${s.name}"?`)) return;
    try {
      await deleteScene(s.id);
      if (localStorage.getItem("plbx_active_board") === s.id) {
        localStorage.removeItem("plbx_active_board");
      }
      setScenes((prev) => prev.filter((x) => x.id !== s.id));
    } catch (e: any) {
      setError(e?.message || "failed to delete");
    }
  };

  return (
    <div className="plbx-boards-layout">
      <WorkspaceSwitcher active={activeWs} onSelect={onSelectWs} />
      <div className="plbx-page plbx-boards-page">
        <div className="plbx-page__inner">
          <header className="plbx-page__header">
            <h1>{activeWsName}</h1>
            <button
              className="plbx-btn plbx-btn--primary"
              onClick={onCreate}
              disabled={creating || !activeWs}
            >
              {creating ? "Creating…" : "+ New board"}
            </button>
          </header>
          <div className="plbx-toolbar">
            <input
              className="plbx-input"
              type="search"
              placeholder="Search boards…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <label className="plbx-checkbox">
              <input
                type="checkbox"
                checked={pinnedOnly}
                onChange={(e) => setPinnedOnly(e.target.checked)}
              />
              Pinned only
            </label>
          </div>

          {error && <div className="plbx-alert plbx-alert--error">{error}</div>}

          {loading ? (
            <div className="plbx-empty">Loading…</div>
          ) : filtered.length === 0 ? (
            <div className="plbx-empty">
              No boards yet. Create one to get started.
            </div>
          ) : (
            <ul className="plbx-board-list">
              {filtered.map((s) => (
                <li key={s.id} className="plbx-board-card">
                  {editingId === s.id ? (
                    <div className="plbx-board-card__edit">
                      <input
                        autoFocus
                        className="plbx-input"
                        value={editDraft}
                        onChange={(e) => setEditDraft(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") commitRename(s);
                          if (e.key === "Escape") cancelRename();
                        }}
                        onBlur={() => commitRename(s)}
                      />
                      <span className="plbx-board-card__meta">
                        Enter — save · Esc — cancel
                      </span>
                    </div>
                  ) : (
                    <button
                      className="plbx-board-card__open"
                      onClick={() => {
                        localStorage.setItem("plbx_pending_board", s.id);
                        window.location.assign("/");
                      }}
                      title={`Open ${s.name}`}
                    >
                      <span className="plbx-board-card__name">
                        {s.pinned && (
                          <span
                            className="plbx-board-card__pin"
                            aria-label="pinned"
                          >
                            ★
                          </span>
                        )}
                        {s.workspaceSlug?.startsWith("personal:") && (
                          <span className="plbx-board-card__lock" aria-label="personal">🔒</span>
                        )}
                        {s.name}
                      </span>
                      <span className="plbx-board-card__meta">
                        {s.workspaceSlug && (
                          <WorkspaceBadge
                            slug={s.workspaceSlug}
                            label={
                              workspaces.find((w) => w.id === s.workspaceId)?.name ?? undefined
                            }
                          />
                        )}
                        Updated {fmtDate(s.updatedAt)}
                      </span>
                    </button>
                  )}
                  <div className="plbx-board-card__actions">
                    <button
                      className="plbx-btn plbx-btn--ghost"
                      onClick={() => onTogglePin(s)}
                      title={s.pinned ? "Unpin" : "Pin"}
                    >
                      {s.pinned ? "Unpin" : "Pin"}
                    </button>
                    <button
                      className="plbx-btn plbx-btn--ghost"
                      onClick={() => startRename(s)}
                      title="Rename"
                    >
                      Rename
                    </button>
                    <button
                      className="plbx-btn plbx-btn--ghost"
                      onClick={() => onDuplicate(s)}
                      disabled={busyId === s.id}
                      title="Duplicate"
                    >
                      {busyId === s.id ? "…" : "Copy"}
                    </button>
                    {isOwner(s) && (
                      <button
                        className="plbx-btn plbx-btn--ghost"
                        onClick={() => setMoveOpen(s)}
                        title="Move to workspace"
                      >
                        Move…
                      </button>
                    )}
                    <button
                      className="plbx-btn plbx-btn--danger"
                      onClick={() => onDelete(s)}
                      title="Delete"
                    >
                      Delete
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {moveOpen && (
        <div
          className="plbx-modal-backdrop"
          role="dialog"
          aria-modal="true"
          onClick={(e) => {
            if (e.target === e.currentTarget) setMoveOpen(null);
          }}
        >
          <div className="plbx-modal">
            <h2>Move "{moveOpen.name}" to:</h2>
            <ul className="plbx-modal__list">
              {workspaces
                .filter((w) => w.id !== moveOpen.workspaceId)
                .map((w) => (
                  <li key={w.id}>
                    <button
                      className="plbx-btn plbx-btn--ghost"
                      onClick={async () => {
                        const scene = moveOpen;
                        setMoveOpen(null);
                        try {
                          await patchScene(scene.id, { workspaceId: w.id });
                          await reload();
                        } catch (e: any) {
                          setError(e?.message || "failed to move");
                        }
                      }}
                    >
                      {w.name}
                    </button>
                  </li>
                ))}
            </ul>
            <div className="plbx-modal__actions">
              <button
                className="plbx-btn plbx-btn--ghost"
                onClick={() => setMoveOpen(null)}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BoardsListPage;
